/**
 * Financial formatting and variance semantics, shared across the project financials workspace.
 *
 * The one rule that matters: a variance is only "good" once you know whether the row is money
 * going out or money coming in. Spending less than planned is favourable; earning less than
 * planned is not. Everything here keys off that.
 */

export const money = (value, { currency = 'EUR', decimals = 0 } = {}) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(value || 0));

export const compactMoney = (value, currency = 'EUR') => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${money(n / 1000000, { currency, decimals: 1 })}M`.replace('.0M', 'M');
  if (abs >= 1000) return `${money(n / 1000, { currency, decimals: 0 })}K`;
  return money(n, { currency });
};

export const percent = (value, decimals = 1) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : `${Number(value).toFixed(decimals)}%`;

export const usedPercent = (actual, budget) =>
  budget ? (Number(actual || 0) / Number(budget)) * 100 : null;

/** Revenue rows read in the opposite direction to cost rows. */
export const isIncomeRow = (row) =>
  row?.account_type === 'revenue' || row?.has_income === true || row?.kind === 'income';

/**
 * Variance of a row, with its meaning.
 * `favourable` is true when reality beat the plan, whichever direction that is for this row.
 */
export const varianceOf = (budget, actual, income = false) => {
  const b = Number(budget || 0);
  const a = Number(actual || 0);
  const diff = a - b;
  const remaining = b - a;
  const pct = b ? (diff / Math.abs(b)) * 100 : null;
  const favourable = income ? diff >= 0 : diff <= 0;
  return {
    diff,
    remaining,
    percent: pct,
    favourable,
    // Within 2% of plan is neither good nor bad news
    neutral: pct !== null && Math.abs(pct) < 2,
  };
};

export const varianceClass = (variance) =>
  variance.neutral ? 'text-foreground-muted' : variance.favourable ? 'text-emerald-600' : 'text-red-600';

/** Signed, human-readable variance: what is left, or what it went over by. */
export const varianceLabel = (variance, currency = 'EUR') => {
  if (!variance.diff) return 'On budget';
  const amount = money(Math.abs(variance.diff), { currency });
  return variance.favourable ? `${amount} under` : `${amount} over`;
};

export const STATUS_PILLS = {
  draft: { label: 'Draft', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  pending_approval: { label: 'Pending', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  posted: { label: 'Posted', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  reversed: { label: 'Reversed', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export const SOURCE_LABELS = {
  manual: 'Manual',
  import: 'Import',
  bank: 'Bank',
};

export const monthLabel = (key) => {
  if (!key) return '—';
  const [year, month] = key.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

export const toCsv = (columns, rows) => {
  const escape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = [columns.map((c) => escape(c.label)).join(',')];
  rows.forEach((row) => lines.push(columns.map((c) => escape(c.value(row))).join(',')));
  return lines.join('\n');
};

export const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
